import Alert from "@mui/joy/Alert";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Stack from "@mui/joy/Stack";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppCTA from "@components/AppCTA/AppCTA";
import AppPasswordInput from "@components/AppPasswordInput/AppPasswordInput";
import GeneralConst from "@consts/GeneralConst";
import RoutesConst from "@consts/RoutesConst";
import UserService from "@services/UserService";
import { login } from "@slices/userSlice.js";
import AlertStrings from "@strings/AlertStrings.json";
import GeneralStrings from "@strings/GeneralStrings.json";
import {
  validateConfirmPassword,
  validatePassword,
} from "@utilities/ValidationHelper";
import LoginLayout from "@pages/LoginPage/LoginLayout";

interface FormValues {
  email: string;
  password: string;
}

interface ResetPasswordFormProps {
  email: string;
  tempPassword: string;
}

interface ResponseData {
  status: string;
  statusMessage?: string;
}

const ResetPasswordForm = ({ email, tempPassword }: ResetPasswordFormProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formStatus, setFormStatus] = useState<string | null>(null);
  const STATUS_CODES = GeneralConst.STATUS_CODES;

  const resetErrorMessages = () => {
    setErrorMessage(null);
    setFormStatus(null);
  };

  const handleSubmit = async (formValues: FormValues) => {
    resetErrorMessages();

    try {
      const response: { data: ResponseData } =
        await UserService.resetPassword(
          email,
          tempPassword,
          formValues.password
        );
      const status = response.data.status;
      if (status === STATUS_CODES.ERROR) {
        setErrorMessage(
          response?.data?.statusMessage || AlertStrings.generalError
        );
        setFormStatus(STATUS_CODES.ERROR);
      } else {
        dispatch(
          login({
            email: formValues.email,
          })
        );
        navigate(RoutesConst.DASHBOARD.DEFAULT);
      }
    } catch (response: unknown) {
      const errorResponse = response as { message?: string };
      setErrorMessage(errorResponse?.message || AlertStrings.generalError);
      setFormStatus(STATUS_CODES.ERROR);
    }
  };

  return (
    <LoginLayout>
      <Stack gap={4} sx={{ mb: 2 }}>
        <Stack gap={1}>
          <h3>{GeneralStrings.form.resetPassword}</h3>
          <p>{GeneralStrings.form.resetPasswordDescription}</p>
        </Stack>
      </Stack>

      {formStatus === STATUS_CODES.ERROR && (
        <Alert
          startDecorator={<i className="fa-solid fa-circle-exclamation" />}
          variant="solid"
          color="danger"
          size="lg"
        >
          {errorMessage}
        </Alert>
      )}

      <Stack gap={4} sx={{ mt: 2 }}>
        <Formik
          initialValues={{
            confirmPassword: "",
            email: email,
            password: "",
          }}
          onSubmit={handleSubmit}
        >
          {({ values, submitForm }) => (
            <Form>
              <FormControl required>
                <FormLabel>{GeneralStrings.form.newPassword}</FormLabel>
                <Field
                  as={AppPasswordInput}
                  name="password"
                  className="form-control"
                  validate={validatePassword}
                  data-testid="password-input"
                />
                <ErrorMessage
                  component="span"
                  className="form-error"
                  name="password"
                />
              </FormControl>
              <FormControl required>
                <FormLabel>
                  {GeneralStrings.form.confirmPassword}
                </FormLabel>
                <Field
                  as={AppPasswordInput}
                  name="confirmPassword"
                  className="form-control"
                  validate={(value: string) =>
                    validateConfirmPassword(values.password, value)
                  }
                  data-testid="confirm-password-input"
                />
                <ErrorMessage
                  component="span"
                  className="form-error"
                  name="confirmPassword"
                />
              </FormControl>
              <Stack gap={4} sx={{ mt: 2 }}>
                <AppCTA
                  type="button"
                  variant="primary"
                  fullWidth
                  label={GeneralStrings.form.submit}
                  onClick={async () => {
                    await submitForm();
                  }}
                />
              </Stack>
            </Form>
          )}
        </Formik>
      </Stack>
    </LoginLayout>
  );
};

export default ResetPasswordForm;
